/**
 * FlashMessages - Portal wrapper bridging server-injected `window.__flash`
 * to lib's `FlashMessages`. Normalizes legacy shape (`type/message`) to
 * lib's shape (`severity/text`) and renders into the MVC `[flash-messages]`
 * slot if present, falling back to lib's default top-of-page portal.
 */

import React, { useEffect, useState } from 'react';
import { FlashMessages as LibFlashMessages, type FlashMessage, type FlashSeverity } from '@/components/ui';

interface RawFlash {
  type?: 'info' | 'warn' | 'error';
  title?: string;
  message: string;
}

const SEVERITY_BY_TYPE: Record<NonNullable<RawFlash['type']>, FlashSeverity> = {
  info: 'info',
  warn: 'warning',
  error: 'error',
};

let flashIdCounter = 0;

const adapt = (raw: RawFlash): FlashMessage => ({
  id: `f-${++flashIdCounter}`,
  severity: SEVERITY_BY_TYPE[raw.type ?? 'info'] ?? 'info',
  title: raw.title,
  text: raw.message,
});

const readFlashFromWindow = (): FlashMessage[] => {
  if (typeof window === 'undefined') return [];
  const raw = window.__flash;
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((e): e is RawFlash => !!e && typeof (e as RawFlash).message === 'string')
    .map((e) => adapt({
      type: e.type ?? 'info',
      title: e.title ? String(e.title) : undefined,
      message: String(e.message),
    }));
};

export interface FlashMessagesProps {
  /** Override target selector (defaults to `[flash-messages]`) */
  targetSelector?: string;
  /** Override messages source (used by tests) */
  messages?: FlashMessage[];
}

export const FlashMessages: React.FC<FlashMessagesProps> = ({
  targetSelector = '[flash-messages]',
  messages,
}) => {
  const [target, setTarget] = useState<Element | null>(null);
  const [entries, setEntries] = useState<FlashMessage[]>(
    () => messages ?? readFlashFromWindow()
  );

  useEffect(() => {
    if (typeof document === 'undefined') return;
    setTarget(document.querySelector(targetSelector));
  }, [targetSelector]);

  useEffect(() => {
    if (messages) setEntries(messages);
  }, [messages]);

  const handleDismiss = (id: string) => {
    setEntries((prev) => prev.filter((e) => e.id !== id));
  };

  if (entries.length === 0) return null;

  return (
    <LibFlashMessages
      messages={entries}
      onDismiss={handleDismiss}
      portalTarget={target}
    />
  );
};

FlashMessages.displayName = 'FlashMessages';

export default FlashMessages;
